import React, { JSX } from "react";
import { startCase } from "lodash";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { CategoricalChartState } from "recharts/types/chart/types";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { BenchmarkingLineOfBusinessEnum, BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { GhostData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { colors, tickFormatter } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { DashboardTile } from "@app/shared/qes-standards";
import { printNumberAsString } from "@app/shared/waffles";

interface ProvidersByNetworkProps {
  canDrillIn: boolean;
  customYAxisTick?: ({ x, y, payload }: { x: number, y: number, payload: { value: string } }) => JSX.Element;
  data: GhostData[]
  loading: boolean;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    isGhost?: boolean,
  ) => void;
  selectedLineOfBusiness: BenchmarkingLineOfBusinessEnum;
  selectedNetwork: string;
  selectedNetworks: BenchmarkingNetworkType[];
  selectedPcaLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum;
}

const Ghosts = (props: ProvidersByNetworkProps): JSX.Element => {
  const onGhostsClick = (data: CategoricalChartState): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!data && props.canDrillIn) {
      const contractData: GhostData = props.data.find(x => x.contractId === data.activeLabel);

      if (!!contractData?.percentage) {
        props.onDashboardDetailsViewClick(BenchmarkingDashboardDetailsTypeEnum.NetworkDrillin, contractData?.providerCount, contractData?.versionId, null, true);
      }
    }
  }

  const customTooltip = (customTooltipProps: TooltipProps<ValueType, string | number>, titleKey: string): JSX.Element => {
    // this accounts for the chart tooltip for the ghost tile only; the custom tooltip for other charts is handled via a utility function
    if (customTooltipProps.payload?.length) {
      const order: number[] = Array.from(Array(customTooltipProps.payload.length)).map((_, index) => index).reverse();
      const tooltipItems: JSX.Element[] = order.map((index) => {
        const payloadItem = customTooltipProps.payload[index];

        return (
          <React.Fragment key={index}>
            <p className="mb-0">
              <span>{startCase(payloadItem.name as string)}:</span> {printNumberAsString(payloadItem.value as number, "0")}%
            </p>
            <p className="mb-0">
              <span>Provider Count:</span> {printNumberAsString(payloadItem.payload.providerCount, "0")}
            </p>
          </React.Fragment>

        );
      });

      let parentOrganizationName;
      let networkName;

      if (!!props.selectedNetworks?.length) {
        const network = props.selectedNetworks.find(x => x.contractId === customTooltipProps.payload[0].payload[titleKey]);

        if (!!network) {
          parentOrganizationName = network.parentOrganization;

          switch (props.selectedLineOfBusiness) {
            case BenchmarkingLineOfBusinessEnum.Medicare:
              networkName = customTooltipProps.payload[0].payload[titleKey];
              break;
            case BenchmarkingLineOfBusinessEnum.Marketplace:
              networkName = (network as QHPNetwork).networkName;
              break;
            default:
              break;
          }
        }
      }

      return (
        <div className="qes-dashboard__custom-tooltip">
          {!!parentOrganizationName && (
            <p className="qes-dashboard__tooltip-title mb-0">{parentOrganizationName}</p>
          )}
          <p className="qes-dashboard__tooltip-title mb-0">{networkName}</p>
          {tooltipItems}
        </div>
      );
    }
  }

  return (
    <DashboardTile
      loading={props.loading}
      subtitle={props.canDrillIn && "Each bar can be clicked to reveal additional details."}
      title={`Network Ghost Percentage - ${props.selectedPcaLineOfBusiness}`}
      widthClass="col-md-12"
    >
      <div style={{ height: "300px", width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            barCategoryGap="20%"
            data={props.data}
            layout="vertical"
            margin={{
              bottom: 0,
              left: 0,
              right: 50, // formatting to prevent the number displayed to right of the longest bar from truncating
              top: 20,
            }}
            onClick={(data: CategoricalChartState) => onGhostsClick(data)}
          >
            <CartesianGrid strokeDasharray="1 0" horizontal={false} />
            <XAxis dataKey="percentage" type="number" tickLine={false} tickFormatter={tickFormatter} />
            <YAxis dataKey="contractId" type="category" tickLine={false} tick={props.customYAxisTick} />
            <Tooltip content={(customTooltipProps) => customTooltip(customTooltipProps, "contractId")} />
            <Bar dataKey="percentage" fill="#8884d8">
              <LabelList
                formatter={(percentage: number) => printNumberAsString(percentage, "0") + "%"}
                dataKey="percentage"
                position="right" // adding the provider count to the side of a bar
                fill="#000000"
              />
              {props.data.map((entry, index) => (
                // custom bar chart colors
                <Cell id={`benchmarking-dashboard__ghosts--contract-${entry.contractId}-percentage-${entry.percentage}`} key={`cell-${index}`} fill={entry.contractId === props.selectedNetwork ? colors.green : colors.lightBlue} />
              ))
              }
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardTile>
  )
}

export default Ghosts;
